import App from '@app/app.js';
import template from './customize-sheet.html';
import './customize-sheet.scss';
import { DELIVERY_TYPE_UTILS } from '@app/utils/ShippingTypeUtils';
import { POPOVER_TEMPLATE_URL, PopoverManager } from '@app/components/core/popover/popover';
import cornerAsideDigitalDownloadHtml from './corner-aside-digital-download.html';
import { DigitalDownloadService } from '@app/service/digital-download-icon/digital-download.service';

const DIGITAL_IMAGE_CATEGORY = 'DgtImages';
const NON_CUSTOMIZABLE_CLASS_MAP = {
  1: 'non-col-100',
  2: 'non-col-50',
  3: 'non-col-33',
  4: 'non-col-25',
  5: 'non-col-20'
};

class CustomizeSheetController {
  canCustomize = true;
  isAddOnPage = false;
  sheetsList = [];
  firstCustomizableSheet = -1;
  firstNonCustomizableSheet = -1;
  amountOfNonCustomizableSheets = 0;
  hasDigitalEligibleItem = false;

  constructor($filter, $scope, $timeout, $templateCache, DeviceService, DigitalDownloadService) {
    this.$filter = $filter;
    this.$timeout = $timeout;
    this.$scope = $scope;
    this.deviceService = DeviceService;
    this.digitalDownloadService = DigitalDownloadService;
    this.popovers = new PopoverManager({
      digitalDownload: {
        getTitle: () => this.$filter('ltTranslateFilter')('DIGITAL_DOWNLOAD_POPOVER_TITLE'),
        getDescription: () => this.$filter('ltTranslateFilter')('DIGITAL_DOWNLOAD_POPOVER_TEXT')
      }
    });
    $templateCache.put('corner-aside-digital-download.html', cornerAsideDigitalDownloadHtml);
  }

  $onInit() {
    this.$scope.$watch(
      () => this.orderLineDetails,
      () => {
        this.createSheets();

        this.hasDigitalEligibleItem = this.orderLineDetails.some(({ item }) =>
          this.digitalDownloadService.getDigitalDownloadType(item)
        );

        this.hideHeader =
          this.sheetsList.every((sheet) => sheet.allowSelection === false) ||
          this.sheetsList.every((sheet) => sheet.allowSelection === true);
        this.onSheetsInit({
          lastSheet: this.getLastSheet()
        });
        this.updateIndexesFor(this.selectedOrderLineDetailsIndex);
        this.amountOfNonCustomizableSheets = this.setMaxWidthForNonCustomizable();
      }
    );
  }

  setMaxWidthForNonCustomizable() {
    const amount = this.sheetsList.filter((sheet) => sheet.allowSelection === false).length;
    return NON_CUSTOMIZABLE_CLASS_MAP[amount] || 'col-min';
  }

  $onChanges(changes) {
    if (!this.isAddOnPage && changes.selectedOrderLineDetailsIndex) {
      const selectedOrderLineDetailsIndex = changes.selectedOrderLineDetailsIndex.currentValue;

      this.updateCanCustomize(this.isCustomizable(selectedOrderLineDetailsIndex));
      if (this.sheetsList.length) {
        this.updateIndexesFor(selectedOrderLineDetailsIndex);
      }
    }
  }

  isCustomizable(orderLineDetailsIndex) {
    const orderLineDetail = this.orderLineDetails[orderLineDetailsIndex];

    return orderLineDetail.item.lookSelectionAllowed;
  }

  getInnerOuterIndexes(orderLineDetailsIndex) {
    const sheets = this.sheetsList.find((el) =>
      el.sheets.some((sheet) => sheet.orderLineDetailsIndex === orderLineDetailsIndex)
    ).sheets;

    const innerIndex = sheets.findIndex((sheet) => sheet.orderLineDetailsIndex === orderLineDetailsIndex);

    return { innerIndex, sheetsLength: sheets.length };
  }

  switchItems(state) {
    this.updateCanCustomize(state);
    this.selectFirstSheet(state);
  }

  updateCanCustomize(state) {
    this.canCustomize = state;
    this.customizable({ state });
  }

  addNewAddon() {
    this.onAddNew();

    this.$timeout(() => {
      const { orderLineDetailsIndex } = this.sheetsList[0].sheets.at(-1);

      this.selectSheet(orderLineDetailsIndex, true);
      this.updateIndexes(orderLineDetailsIndex, this.sheetsList[0].sheets.length);
    });
  }

  removeAddon(index) {
    this.onRemoveAddOn({ index });

    this.$timeout(() => {
      const newIndex = index === 0 ? index : index - 1;
      this.selectSheet(newIndex, true);
      this.updateIndexes(newIndex, this.sheetsList[0].sheets.length);
    });
  }

  selectFirstSheet(customizable) {
    const firstSheet = customizable ? this.firstCustomizableSheet : this.firstNonCustomizableSheet;

    this.selectSheet(firstSheet.orderLineDetailsIndex);
  }

  selectSheet(orderLineDetailsIndex, keepModalClose = false) {
    this.onSelectSheet({ orderLineDetailsIndex, keepModalClose });
  }

  updateIndexesFor(orderLineDetailsIndex) {
    const { innerIndex, sheetsLength } = this.getInnerOuterIndexes(orderLineDetailsIndex);
    this.updateIndexes(innerIndex, sheetsLength);
  }

  updateIndexes(position, amount) {
    this.onUpdateInnerOuterIndexes({ payload: { position: position + 1, amount } });
  }

  removeStudent() {
    this.onRemoveStudent();
  }

  createSheets() {
    const firstDigitalDownloads = this.digitalDownloadService.getFirstDigitalDownloads(this.orderLineDetails);

    const getByGroup = this.orderLineDetails.reduce((acc, orderLineDetail, index) => {
      const { item, look, previewImageNon } = orderLineDetail;

      if (!item?.nameDesc || !look) return acc;

      const existing = acc[item.nameDesc] || {
        count: 0,
        isDigital: this.isDigital(item),
        name: this.isDigital(item) ? this.getItemName(item, item.nameDesc) : item.nameDesc,
        allowSelection: item.lookSelectionAllowed,
        sheets: []
      };

      existing.sheets.push({
        item,
        look,
        previewImageNon,
        orderLineDetailsIndex: index,
        displayDigitalDownload: firstDigitalDownloads.get(orderLineDetail)
      });
      acc[item.nameDesc] = existing;
      return acc;
    }, {});

    this.sheetsList = Object.values(getByGroup).map((el) => {
      if (el.sheets.length === 1) {
        return el;
      }
      const [count, name] = el.name.split(' - ');
      return {
        ...el,
        name: el.isDigital ? `${el.sheets.length} - ${el.name}` : `${count * el.sheets.length} - ${name}`
      };
    });

    this.firstCustomizableSheet = this.getFirstSheet(true);
    this.firstNonCustomizableSheet = this.getFirstSheet(false);
    this.addClassForNonCustomizableItems();
  }

  getFirstSheet(customizable) {
    return this.sheetsList.find((el) => el.allowSelection === customizable)?.sheets[0];
  }

  getLastSheet() {
    let sheets = this.sheetsList.findLast((el) => !el.allowSelection)?.sheets;

    if (!sheets) {
      sheets = this.sheetsList.findLast((el) => el.allowSelection)?.sheets;
    }

    if (sheets) {
      return sheets[sheets.length - 1];
    }
  }

  addClassForNonCustomizableItems() {
    this.$timeout(() => {
      document.querySelector('.unselectable')?.classList.add('first-unselectable');
    }); //need to wait for the DOM to render
  }

  getItemName(item, description) {
    if (item?.isDigital || this.isDigital(item)) {
      return this.$filter('ltTranslateFilter')('DIGITAL_IMAGE');
    }
    return description;
  }

  isDigital(item) {
    return item?.productCategory === DIGITAL_IMAGE_CATEGORY && item.lookSelectionAllowed;
  }

  getPopoverTemplate() {
    return POPOVER_TEMPLATE_URL;
  }

  showHeader() {
    return !(this.isAddOnPage || this.hideHeader || this.deviceService.isMobile());
  }
}

CustomizeSheetController.$inject = [
  '$filter',
  '$scope',
  '$timeout',
  '$templateCache',
  'DeviceService',
  'DigitalDownloadService'
];

App.component('ltCustomizeSheet', {
  template,
  bindings: {
    orderLineDetails: '<',
    selectedOrderLineDetailsIndex: '<',
    showNotMyChildAction: '<',
    isAddOnPage: '<',
    onSelectSheet: '&',
    customizable: '&',
    onRemoveStudent: '&',
    onAddNew: '&',
    onRemoveAddOn: '&',
    onUpdateInnerOuterIndexes: '&',
    onSheetsInit: '&',
    bearerTokenUrl: '<'
  },
  controller: CustomizeSheetController,
  controllerAs: 'sheet'
});
